import { zodResolver } from '@hookform/resolvers/zod';
import { Accordion, Button, cn, Dialog, Text } from '@risksmart-app/atomic-ui';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import {
  type DefaultValues,
  type FieldValues,
  FormProvider,
  useForm,
} from 'react-hook-form';
import type { ZodType } from 'zod';

import type { ConditionalLogicOption } from '../types';
import { useFormEditorDialogStore } from '../useFormEditorDialogStore';
import { ConditionalLogicRuleBuilder } from './ConditionalLogicRuleBuilder';
import { GuidanceField } from './GuidanceField';
import { SlideOutDiscardButton } from './SlideOutDiscardButton';
import { SwitchSectionField } from './SwitchSectionField';

export interface SubEditorDialogProps<T extends FieldValues> {
  open: boolean;
  onCancel: () => void;
  title: string;
  saveLabel: string;
  cancelLabel: string;
  schema: ZodType<T>;
  defaultValues: DefaultValues<T>;
  onSubmit: (data: T) => void;
  initialOpenSection: string;
  configCard: ReactNode;
  fieldOptions: ConditionalLogicOption[];
  getValueOptions?: (fieldValue: string) => ConditionalLogicOption[];
  currentEditingId?: string;
  /**
   * Field ids that would create a cycle if picked as a rule source.
   * Filtered out of the "If" picker before render.
   */
  disallowedCycleCandidates?: Set<string>;
  conditionalLogicDescription?: string;
  guidancePlaceholder?: string;
  showGuidance?: boolean;
  showConditionalLogic?: boolean;
  /**
   * Locks the conditional-logic section: the enable toggle and rule builder
   * render read-only. Used for strict-replace read-only default conditions.
   */
  conditionalLogicLocked?: boolean;
  /**
   * Disables only the enable toggle (not the rule builder). Used by merge-mode
   * defaults: locked rules keep the section on (you can't turn it off), but the
   * admin can still add and edit their own rules via the per-row builder.
   */
  conditionalLogicToggleLocked?: boolean;
  /**
   * Explanation rendered beside a locked conditional-logic toggle. Shown in the
   * trigger rather than the content because a locked-off toggle can never be
   * expanded, so a note inside the accordion body would never be read.
   */
  conditionalLogicDisabledReason?: string;
}

export const SubEditorDialog = <T extends FieldValues>({
  open,
  onCancel,
  title,
  saveLabel,
  cancelLabel,
  schema,
  defaultValues,
  onSubmit,
  initialOpenSection,
  configCard,
  fieldOptions,
  getValueOptions,
  currentEditingId,
  disallowedCycleCandidates,
  conditionalLogicDescription,
  guidancePlaceholder,
  showGuidance = true,
  showConditionalLogic = true,
  conditionalLogicLocked = false,
  conditionalLogicToggleLocked = false,
  conditionalLogicDisabledReason,
}: SubEditorDialogProps<T>) => {
  const form = useForm<T>({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onSubmit',
  });

  // Use ref so the effect always reads the latest defaults without
  // needing defaultValues in the dependency array
  const defaultValuesRef = useRef(defaultValues);
  defaultValuesRef.current = defaultValues;

  useEffect(() => {
    if (open) {
      form.reset(defaultValuesRef.current);
      useFormEditorDialogStore.setState({
        openSections: [initialOpenSection],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openSections = useFormEditorDialogStore((s) => s.openSections);
  const setOpenSections = useFormEditorDialogStore((s) => s.setOpenSections);

  const isDirty = form.formState.isDirty;
  const conditionalLogicError = (
    form.formState.errors as Record<string, { root?: { message?: string } }>
  ).conditionalLogicRules?.root?.message;
  const [showDiscard, setShowDiscard] = useState(false);

  // Reset discard confirmation when the dialog opens or form becomes clean
  useEffect(() => {
    if (!isDirty) {
      setShowDiscard(false);
    }
  }, [isDirty]);

  const handleCancelClick = () => {
    if (isDirty) {
      setShowDiscard(!showDiscard);
    } else {
      onCancel();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          if (isDirty) {
            setShowDiscard(!showDiscard);
          } else {
            onCancel();
          }
        }
      }}
      size={'xl'}
    >
      <Dialog.Header title={title} />
      <Dialog.Body className={'flex flex-col gap-4 p-4'}>
        <FormProvider {...form}>
          <Accordion
            multiple
            value={openSections}
            onValueChange={setOpenSections}
          >
            {configCard}
            {showConditionalLogic ? (
              <SwitchSectionField
                value={'conditional-logic'}
                title={'Conditional logic'}
                name={'conditionalLogicEnabled'}
                disabled={
                  conditionalLogicLocked || conditionalLogicToggleLocked
                }
                disabledReason={conditionalLogicDisabledReason}
                className={
                  conditionalLogicError ? 'border-destructive' : undefined
                }
              >
                <ConditionalLogicRuleBuilder
                  fieldOptions={fieldOptions}
                  getValueOptions={getValueOptions}
                  currentEditingId={currentEditingId}
                  disallowedCycleCandidates={disallowedCycleCandidates}
                  description={conditionalLogicDescription}
                  errorMessage={conditionalLogicError}
                  locked={conditionalLogicLocked}
                />
              </SwitchSectionField>
            ) : null}
            {showGuidance ? (
              <SwitchSectionField
                value={'guidance'}
                title={'Guidance'}
                name={'guidanceEnabled'}
              >
                <GuidanceField placeholder={guidancePlaceholder} />
              </SwitchSectionField>
            ) : null}
          </Accordion>
        </FormProvider>
      </Dialog.Body>
      <Dialog.Footer className={'flex items-center justify-between'}>
        <div className={cn('flex items-center gap-2')}>
          <Button onClick={form.handleSubmit(onSubmit)}>{saveLabel}</Button>
          <Button
            variant={'neutral'}
            style={'outline'}
            onClick={handleCancelClick}
          >
            {cancelLabel}
          </Button>
          <SlideOutDiscardButton visible={showDiscard} onDiscard={onCancel} />
        </div>
        <Text
          preset={'body-md'}
          className={cn(
            'text-sm text-neutral-muted-foreground transition-opacity duration-300',
            isDirty ? 'opacity-100' : 'opacity-0'
          )}
          render={<span />}
        >
          {'You have unsaved changes'}
        </Text>
      </Dialog.Footer>
    </Dialog>
  );
};
